/**
 * The placement camera's easing (owner, 2026-09-23: "the camera needs to smoothly follow the road as you're placing
 * it"). A critically damped spring — Kelly's "Critically Damped Ease-In/Ease-Out Smoothing" (Game Programming Gems
 * 4), the curve behind Unity's SmoothDamp — so a glide starts at once, never overshoots and lands softly, and a new
 * target mid-glide bends the motion instead of restarting it. Pure maths, so it is unit-tested in Node.
 */
export const CameraFollow = {
  /**
   * Spring time constant, in seconds. The error after t is (1 + ωt)·e^(−ωt) with ω = 2 / SmoothTime, so the glide is
   * within 1 % of its target after ~3.3 × SmoothTime: 0.13 s gives the ~400 ms ease-out the placement asks for.
   */
  SmoothTime: 0.13,
  /** Reduced effects: the same curve, over about 150 ms — the view still gets there, it just does not travel. */
  ReducedSmoothTime: 0.045,
  /** A glide shorter than this (cells) is not worth moving the view for; the view stays put rather than twitch. */
  DeadZone: 0.35,
  /** Zoom changes smaller than this (as a share of the zoom) are left alone for the same reason. */
  ZoomDeadZone: 0.03,
  /** A glide is over when the look point is this close (cells) and the zoom this close (share) to the target. */
  SettleCells: 0.01,
  SettleZoom: 0.002,
  /** Hard stop, in seconds: if the target cannot be reached (the map edge moved under it), the view gives up. */
  MaxSeconds: 1.5,
  /** Room kept between what is framed and the edges of the visible band, in CSS pixels (about a fingertip). */
  MarginPx: 36,

  /**
   * One step of the spring: where `current` moves towards `target` in `dt` seconds, and its new velocity. Exact for
   * any dt (an implicit step), so a dropped frame lengthens the step without making it overshoot.
   */
  damp(current: number, target: number, velocity: number, smoothTime: number, dt: number): { value: number; velocity: number } {
    const omega = 2 / Math.max(1e-4, smoothTime);
    const x = omega * dt;
    const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);   // Padé approximation of e^(−x)
    const change = current - target;
    const temp = (velocity + omega * change) * dt;
    let value = target + (change + temp) * decay;
    let v = (velocity - omega * temp) * decay;
    // The approximation can step a hair past the target on a very long frame: land on it instead.
    if ((target - current > 0) === (value > target)) { value = target; v = 0; }
    return { value, velocity: v };
  },

  /**
   * How far the look point must move, in screen pixels, so a box of screen points `[minX, maxX] × [minY, maxY]` sits
   * inside `band` (the uncovered part of the screen, less the margin). `centre` asks for the box centred in the band;
   * otherwise it only moves as far as it must, and not at all when the box is already inside. A box bigger than the
   * band is centred either way (the caller zooms out for it).
   */
  shiftToFit(box: ScreenBox, band: ScreenBox, centre: boolean): { dx: number; dy: number } {
    const axis = (lo: number, hi: number, bLo: number, bHi: number): number => {
      if (centre || hi - lo > bHi - bLo) return (lo + hi) / 2 - (bLo + bHi) / 2;
      if (lo < bLo) return lo - bLo;
      if (hi > bHi) return hi - bHi;
      return 0;
    };
    return { dx: axis(box.minX, box.maxX, band.minX, band.maxX), dy: axis(box.minY, box.maxY, band.minY, band.maxY) };
  },

  /** How many times farther the camera must stand for the box to fit in the band (1 when it already fits). */
  zoomToFit(box: ScreenBox, band: ScreenBox): number {
    const w = (box.maxX - box.minX) / Math.max(1, band.maxX - band.minX);
    const h = (box.maxY - box.minY) / Math.max(1, band.maxY - band.minY);
    return Math.max(1, w, h);
  },
};

export interface ScreenBox { readonly minX: number; readonly maxX: number; readonly minY: number; readonly maxY: number }
